/** Estado global online/offline (navigator + verificación periódica). */
import { reactive } from "vue";
import { API_BASE_URL } from "@/config/api";

const listeners = new Set();
const LEGACY_OFFLINE_FLAG_KEY = "puestify-offline";

/** Rutas del panel que funcionan sin internet (POS + ventas locales). */
export const OFFLINE_ALLOWED_PATHS = [
  "/u/pos",
  "/u/cobros",
  "/u/home",
  "/u/productos",
  "/u/etiquetas",
  "/u/stock",
  "/u/nuevoProducto",
  "/u/mesas",
  "/u/caja",
];

let probeTimer = null;
let probeInFlight = null;

function readNavigatorOnline() {
  return typeof navigator !== "undefined" ? !!navigator.onLine : true;
}

function clearLegacyOfflineFlag() {
  try {
    sessionStorage.removeItem(LEGACY_OFFLINE_FLAG_KEY);
  } catch (_) {
    /* ignore */
  }
}

/** Objeto reactivo compartido (compatible con Options API y Composition API). */
export const connectivity = reactive({
  online: readNavigatorOnline(),
});

export function isOnline() {
  return connectivity.online;
}

export function isOffline() {
  return !connectivity.online;
}

export function isOfflineAllowedPath(path) {
  const normalized = String(path || "")
    .split("?")[0]
    .split("#")[0];
  return OFFLINE_ALLOWED_PATHS.some(
    (allowed) => normalized === allowed || normalized.startsWith(`${allowed}/`),
  );
}

export function onConnectivityChange(fn) {
  listeners.add(fn);
  fn(connectivity.online);
  return () => listeners.delete(fn);
}

function notifyListeners(online) {
  listeners.forEach((fn) => {
    try {
      fn(online);
    } catch (_) {
      /* ignore */
    }
  });
}

function setOnlineState(next, forceNotify = false) {
  const changed = connectivity.online !== next;
  connectivity.online = next;
  if (changed || forceNotify) {
    notifyListeners(next);
  }
}

async function probeServerReachable() {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 5000);
    const res = await fetch(`${API_BASE_URL}/config/public`, {
      method: "GET",
      cache: "no-store",
      signal: ctrl.signal,
    });
    clearTimeout(timer);
    return res.ok;
  } catch (_) {
    return false;
  }
}

function scheduleRecoveryProbe() {
  clearTimeout(probeTimer);
  if (typeof document === "undefined" || document.visibilityState === "hidden")
    return;
  if (connectivity.online) return;
  probeTimer = setTimeout(() => {
    void refreshConnectivity({ force: true });
  }, 4000);
}

function syncFromNavigator(forceNotify = false) {
  clearLegacyOfflineFlag();
  setOnlineState(readNavigatorOnline(), forceNotify);
  if (!connectivity.online) {
    scheduleRecoveryProbe();
  }
}

/**
 * Relee navigator y verifica reachability del API.
 * @param {{ force?: boolean }} [options] - `force: true` prueba el servidor aunque
 *   `navigator.onLine` diga offline (útil cuando el navegador no detecta la red).
 * @returns {Promise<boolean>} estado online resultante
 */
export async function refreshConnectivity(options = {}) {
  const force = !!(options && options.force);
  clearLegacyOfflineFlag();

  if (!force && !readNavigatorOnline()) {
    setOnlineState(false, true);
    return connectivity.online;
  }

  if (probeInFlight) {
    await probeInFlight;
    return connectivity.online;
  }

  probeInFlight = (async () => {
    const reachable = await probeServerReachable();

    setOnlineState(reachable, force);

    if (!reachable) {
      scheduleRecoveryProbe();
    }
  })();

  try {
    await probeInFlight;
  } finally {
    probeInFlight = null;
  }
  return connectivity.online;
}

export function initConnectivity() {
  if (typeof window === "undefined") return;

  clearLegacyOfflineFlag();
  connectivity.online = readNavigatorOnline();

  window.addEventListener("online", () => {
    void refreshConnectivity();
  });
  window.addEventListener("offline", () => {
    setOnlineState(false, true);
    clearTimeout(probeTimer);
  });
  window.addEventListener("pageshow", () => {
    void refreshConnectivity();
  });
  window.addEventListener("load", () => {
    void refreshConnectivity();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void refreshConnectivity();
    }
  });

  window.setInterval(() => {
    if (document.visibilityState !== "visible") return;
    if (connectivity.online) {
      syncFromNavigator(false);
      return;
    }
    // Offline: reintentar aunque navigator diga offline (falsos negativos).
    void refreshConnectivity({ force: true });
  }, 8000);

  void refreshConnectivity();
}
